import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { workforceGuardrailRuleLabels } from "src/queries/workforceGuardrailRuleLabels.graphql";

import type {
  GuardrailCategory,
  GuardrailCheck,
  GuardrailEvaluation,
} from "./RequisitionGuardrailOutcome";

export type RuleLabel = {
  id: string;
  name?: string | null;
  description?: string | null;
  category?: GuardrailCategory | null;
  ownerRole?: string | null;
};

export type RuleLabelsData = {
  workforceGuardrailMonitor?: {
    rules?: Array<RuleLabel | null> | null;
  } | null;
};

export const ruleLabelsOf = (data: RuleLabelsData | undefined): RuleLabel[] =>
  (data?.workforceGuardrailMonitor?.rules ?? []).flatMap((rule) =>
    rule?.id ? [rule] : []
  );

const ruleKey = (id: string): string => id.trim().toLowerCase();

/** A stored name that is only the rule id is not a name. */
export const realRuleName = (check: {
  name?: string | null | undefined;
  ruleId: string;
}): string => {
  const name = check.name?.trim() ?? "";
  if (name === "" || ruleKey(name) === ruleKey(check.ruleId)) {
    return "";
  }
  return name;
};

/** Name, or "Unnamed rule" when the name was not stored. Never the id. */
export const displayRuleName = (
  name: string | null | undefined,
  id: string
): string => realRuleName({ name, ruleId: id }) || "Unnamed rule";

/**
 * Title for one result row.
 * "Deleted rule" — the rule list loaded and this id is not in it.
 * "Unnamed rule" — the list has not loaded, or the rule has no name.
 */
export const checkTitle = (
  check: { name?: string | null | undefined; ruleId: string },
  rules: RuleLabel[],
  catalogueLoaded: boolean
): string => {
  const stamped = realRuleName(check);
  if (stamped) {
    return stamped;
  }
  const rule = rules.find((item) => ruleKey(item.id) === ruleKey(check.ruleId));
  const lookedUp = rule?.name?.trim() ?? "";
  if (lookedUp && ruleKey(lookedUp) !== ruleKey(check.ruleId)) {
    return lookedUp;
  }
  if (rule) {
    return "Unnamed rule";
  }
  if (catalogueLoaded) {
    return "Deleted rule";
  }
  return "Unnamed rule";
};

export const withCheckLabels = (
  check: GuardrailCheck,
  label: {
    name?: string | null;
    description?: string | null;
    category?: GuardrailCategory | null;
    ownerRole?: string | null;
  }
): GuardrailCheck => {
  const name = label.name?.trim();
  const usable = name && ruleKey(name) !== ruleKey(check.ruleId) ? name : "";
  const description = label.description?.trim();
  const ownerRole = label.ownerRole?.trim();
  return {
    ...check,
    ...(usable ? { name: usable } : {}),
    ...(description ? { description } : {}),
    ...(label.category ? { category: label.category } : {}),
    ...(ownerRole ? { ownerRole } : {}),
  };
};

export const useGuardrailRuleLabels = (
  enabled: boolean
): { rules: RuleLabel[]; ready: boolean; loaded: boolean } => {
  const { data, error } = useQuery<RuleLabelsData>(
    workforceGuardrailRuleLabels,
    {
      skip: !enabled,
      fetchPolicy: "cache-and-network",
    }
  );
  const rules = useMemo(() => ruleLabelsOf(data), [data]);
  const ready = !enabled || data != null || error != null;
  const loaded = enabled && data != null && error == null;
  return { rules, ready, loaded };
};

/** Join monitor rule words onto a stamp. Match ids ignoring letter case. */
export const withRuleLabels = (
  evaluation: GuardrailEvaluation | null | undefined,
  rules: RuleLabel[]
): GuardrailEvaluation | null | undefined => {
  if (!evaluation || rules.length === 0) {
    return evaluation;
  }
  const byId = new Map(rules.map((rule) => [ruleKey(rule.id), rule]));
  return {
    ...evaluation,
    checks: evaluation.checks.map((check) => {
      const rule = byId.get(ruleKey(check.ruleId));
      if (!rule) {
        return check;
      }
      return withCheckLabels(check, rule);
    }),
  };
};
